import { CopyButton, Group, Menu } from '@mantine/core';
import {
  IconBallpen,
  IconCheck,
  IconCopy,
  IconDotsCircleHorizontal,
  IconDownload,
  IconFiles,
  IconPrinter,
  IconSend,
  IconSettings,
  IconShare,
} from '@tabler/icons';
import { FC, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ImportButton from '../../../Molecules/Lexoffice/ImportButton';
import SevdeskSyncButton from '../../../Molecules/Event/Documents/SevdeskSyncButton';
import { ProfileContext } from '../../../Service/Context/ProfileContext';
import { useBookitupScope } from '../../../Service/Context/BookitupSubscriptionContext';
import EmailImportButton from '../../../Molecules/Lexoffice/EmailImportButton';
import { LexOfficeS } from '../../../Service/LexOfficeS';
import ActionIconWithText from './ActionIconWithText';
import InjectableFooter from './InjectableFooter';

interface Props {
  context: {
    published: boolean;
    entity: any;
    portalSupported: boolean;
    isPrintable: boolean;
    portalLink: string | null;
    editable: boolean;
    isInvoice?: boolean;
  };
  handlers: {
    publish: () => void;
    sendViaEmail: () => void;
    downloadDocument: () => void;
    printDocument: () => void;
    setLastModified: (val: Date) => void;
    editDocument: () => void;
    markAsReady?: () => void;
  };
}

const EntityPreviewFooter: FC<Props> = ({ context, handlers }) => {
  const { published, portalSupported, portalLink, isInvoice, entity } = context;
  const { profile } = useContext(ProfileContext);
  const { publish, sendViaEmail, downloadDocument, printDocument, setLastModified, editDocument, markAsReady } =
    handlers;

  const { id: eventId } = useParams();
  const navigate = useNavigate();
  const { accountingTool } = profile ?? {};
  const { isScopeSelected: hasDocumentsScope } = useBookitupScope('DOCUMENTS');
  const { isScopeSelected: hasEmailScope } = useBookitupScope('EMAILS');

  return (
    <InjectableFooter>
      <Group px="xl" pt={5} sx={{ justifyContent: 'space-between' }}>
        <ActionIconWithText
          isVisible={hasDocumentsScope}
          label="Dateien"
          onClick={() => navigate(`/events/${eventId}/documents`)}
        >
          <IconFiles />
        </ActionIconWithText>
        <ActionIconWithText isVisible={hasDocumentsScope} label="Bearbeiten" onClick={editDocument}>
          <IconBallpen />
        </ActionIconWithText>
        <ActionIconWithText isVisible={hasEmailScope} label="Mail" onClick={sendViaEmail}>
          <IconSend />
        </ActionIconWithText>
        {!published && (
          <ActionIconWithText disabled={!portalSupported} label="Teilen" onClick={publish}>
            <IconShare />
          </ActionIconWithText>
        )}
        {published && portalLink && (
          <CopyButton value={portalLink} timeout={2000}>
            {({ copied, copy }) => (
              <ActionIconWithText label="Link kopieren" color={copied ? 'teal' : 'gray'} onClick={copy}>
                {copied ? <IconCheck /> : <IconCopy />}
              </ActionIconWithText>
            )}
          </CopyButton>
        )}
        <Menu withArrow shadow="md" width={230} transition="slide-up" position="top-end">
          <Menu.Target>
            <div>
              <ActionIconWithText label="Mehr">
                <IconDotsCircleHorizontal />
              </ActionIconWithText>
            </div>
          </Menu.Target>
          <Menu.Dropdown>
            <>
              {isInvoice && (
                <>
                  {accountingTool === 'LexOffice' && (
                    <ImportButton dropDownVesion bookingDocument={entity} setLastModified={setLastModified} />
                  )}
                  {accountingTool === 'sevDesk' && (
                    <SevdeskSyncButton dropDownVesion bookingDocument={entity} setLastModified={setLastModified} />
                  )}
                  {accountingTool && LexOfficeS.isEmailAccountingTool(accountingTool) && (
                    <EmailImportButton dropDownVesion bookingDocument={entity} />
                  )}
                  {!accountingTool && (
                    <Menu.Item icon={<IconSettings size={14} />} onClick={() => navigate('/settings/bookkeeping')}>
                      an Buchhaltung senden
                    </Menu.Item>
                  )}
                </>
              )}
              {markAsReady && (
                <Menu.Item icon={<IconCheck size={14} />} onClick={() => markAsReady()}>
                  Fertigstellen
                </Menu.Item>
              )}
              <Menu.Item icon={<IconDownload size={14} />} onClick={downloadDocument}>
                Herunterladen
              </Menu.Item>
              <Menu.Item icon={<IconPrinter size={14} />} onClick={printDocument}>
                Drucken
              </Menu.Item>
            </>
          </Menu.Dropdown>
        </Menu>
      </Group>
    </InjectableFooter>
  );
};

export default EntityPreviewFooter;
